import { useEffect, useRef, useState } from "react";

const STATS = [
  {
    label: "Audience experience",
    value: 800,
    suffix: "K+",
    sub: "Combined audience our team has helped grow.",
    testid: "stat-audience",
  },
  {
    label: "Monthly views",
    value: 100,
    suffix: "M+",
    sub: "Across managed accounts and partner campaigns.",
    testid: "stat-monthly-views",
  },
  {
    label: "Years in market",
    value: 6,
    suffix: "+",
    sub: "Across social media, music marketing, and brand strategy.",
    testid: "stat-years",
  },
];

const useCountUp = (target, durationMs = 1800, start = false) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    let raf = 0;
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs, start]);
  return val;
};

const StatItem = ({ stat, started, idx }) => {
  const value = useCountUp(stat.value, 1600, started);
  return (
    <div
      data-testid={stat.testid}
      className="relative flex flex-col gap-3 py-12 md:py-16 px-6 md:px-10 border-t border-white/10 first:border-t-0 md:border-t-0 md:border-l md:first:border-l-0"
    >
      <span className="text-xs uppercase tracking-[0.28em] text-neutral-500 font-medium">
        0{idx + 1}
      </span>
      <div className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tighter font-light text-white break-words">
        {value}
        <span className="text-white/70">{stat.suffix}</span>
      </div>
      <div className="font-display text-lg md:text-xl text-white font-medium tracking-tight">
        {stat.label}
      </div>
      <p className="text-sm md:text-base text-neutral-400 max-w-xs">{stat.sub}</p>
    </div>
  );
};

export const Stats = () => {
  const [started, setStarted] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setStarted(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      data-testid="stats-section"
      ref={sectionRef}
      className="relative bg-[#050505] py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-20">
          <div>
            <span className="text-xs uppercase tracking-[0.28em] text-neutral-500 font-medium">
              03 / By the numbers
            </span>
            <h2
              data-testid="stats-heading"
              className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-light tracking-tighter leading-[1.02]"
            >
              Built on
              <br />
              <span className="italic font-medium">real reach.</span>
            </h2>
          </div>
          <p className="max-w-sm text-base md:text-lg text-neutral-400">
            Our team has spent years building audiences, marketing music, and turning
            attention into revenue.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] grid grid-cols-1 md:grid-cols-3 overflow-hidden">
          {STATS.map((s, i) => (
            <StatItem key={s.label} stat={s} started={started} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Stats;
